// Utilità per la gestione delle immagini
export const imageUtils = {
  // Comprime un'immagine canvas in formato JPEG/WebP
  compressImage: (canvas, quality = 0.8, type = 'image/jpeg') => {
    return canvas.toDataURL(type, quality);
  },
  
  // Carica un'immagine da URL
  loadImage: (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Errore caricamento immagine: ${url}`));
      img.src = url;
    });
  },
  
  // Ridimensiona un'immagine mantenendo le proporzioni
  resizeImage: (img, maxWidth, maxHeight) => {
    const canvas = document.createElement('canvas');
    let width = img.width;
    let height = img.height;
    
    if (width > height) {
      if (width > maxWidth) {
        height = Math.round(height * maxWidth / width);
        width = maxWidth;
      }
    } else {
      if (height > maxHeight) {
        width = Math.round(width * maxHeight / height);
        height = maxHeight;
      }
    }
    
    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    
    return canvas;
  },
  
  // Crea un'immagine composita combinando due immagini
  createComposite: async (oldImageUrl, newImageUrl, template, logoUrl, text) => {
    try {
      // Carica tutte le immagini
      const [oldImg, newImg, templateImg, logoImg] = await Promise.all([
        imageUtils.loadImage(oldImageUrl),
        imageUtils.loadImage(newImageUrl),
        template ? imageUtils.loadImage(template) : null,
        logoUrl ? imageUtils.loadImage(logoUrl) : null
      ]);
      
      // Crea canvas con dimensioni 9:16 (per storie Instagram)
      const canvas = document.createElement('canvas');
      canvas.width = 1080;  // Larghezza standard
      canvas.height = 1920; // Altezza per rapporto 9:16
      
      const ctx = canvas.getContext('2d');
      
      // Disegna lo sfondo/template se presente
      if (templateImg) {
        ctx.drawImage(templateImg, 0, 0, canvas.width, canvas.height);
      } else {
        // Sfondo nero come fallback
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      // Posiziona la foto vecchia nella parte superiore
      const oldImgHeight = canvas.height * 0.4; // 40% dell'altezza
      const oldImgWidth = oldImgHeight * (oldImg.width / oldImg.height);
      const oldImgX = (canvas.width - oldImgWidth) / 2;
      const oldImgY = canvas.height * 0.1; // 10% dall'alto
      
      ctx.drawImage(oldImg, oldImgX, oldImgY, oldImgWidth, oldImgHeight);
      
      // Posiziona la foto nuova nella parte inferiore
      const newImgHeight = canvas.height * 0.4; // 40% dell'altezza
      const newImgWidth = newImgHeight * (newImg.width / newImg.height);
      const newImgX = (canvas.width - newImgWidth) / 2;
      const newImgY = canvas.height * 0.5; // 50% dall'alto
      
      ctx.drawImage(newImg, newImgX, newImgY, newImgWidth, newImgHeight);
      
      // Aggiungi bordi alle immagini
      ctx.strokeStyle = '#daa520'; // Oro
      ctx.lineWidth = 5;
      ctx.strokeRect(oldImgX, oldImgY, oldImgWidth, oldImgHeight);
      ctx.strokeRect(newImgX, newImgY, newImgWidth, newImgHeight);
      
      // Aggiungi testo "Prima" e "Dopo"
      ctx.font = 'bold 48px MedievalSharp, serif';
      ctx.fillStyle = '#daa520'; // Oro
      ctx.textAlign = 'center';
      
      ctx.fillText('Prima', canvas.width / 2, oldImgY - 20);
      ctx.fillText('Dopo', canvas.width / 2, newImgY - 20);
      
      // Aggiungi logo se presente
      if (logoImg) {
        const logoSize = canvas.width * 0.2; // 20% della larghezza
        const logoX = (canvas.width - logoSize) / 2;
        const logoY = canvas.height * 0.02; // 2% dall'alto
        
        ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
      }
      
      // Aggiungi testo in fondo
      if (text) {
        ctx.font = 'bold 36px MedievalSharp, serif';
        ctx.fillStyle = '#daa520'; // Oro
        ctx.textAlign = 'center';
        ctx.fillText(text, canvas.width / 2, canvas.height * 0.95);
      }
      
      return canvas.toDataURL('image/jpeg', 0.9);
      
    } catch (error) {
      console.error('Errore nella creazione dell\'immagine composita:', error);
      throw error;
    }
  }
};

// Utilità per gestione storage locale
export const storageUtils = {
  // Salva dati nel localStorage
  saveToLocalStorage: (key, data) => {
    try {
      const serializedData = JSON.stringify(data);
      localStorage.setItem(key, serializedData);
      return true;
    } catch (error) {
      console.error('Errore durante il salvataggio nel localStorage:', error);
      return false;
    }
  },
  
  // Recupera dati dal localStorage
  getFromLocalStorage: (key) => {
    try {
      const serializedData = localStorage.getItem(key);
      if (serializedData === null) {
        return null;
      }
      return JSON.parse(serializedData);
    } catch (error) {
      console.error('Errore durante il recupero dal localStorage:', error);
      return null;
    }
  },
  
  // Rimuove dati dal localStorage
  removeFromLocalStorage: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Errore durante la rimozione dal localStorage:', error);
      return false;
    }
  },
  
  // Verifica se il localStorage è disponibile
  isLocalStorageAvailable: () => {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  },
  
  // Salva immagine in cache locale (utilizza IndexedDB tramite un wrapper semplificato)
  cacheImage: async (key, imageDataUrl) => {
    try {
      if (!('indexedDB' in window)) {
        throw new Error('IndexedDB non supportato dal browser');
      }
      
      return new Promise((resolve, reject) => {
        const request = indexedDB.open('CelticDruidMuseum', 1);
        
        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('images')) {
            db.createObjectStore('images', { keyPath: 'id' });
          }
        };
        
        request.onsuccess = (event) => {
          const db = event.target.result;
          const transaction = db.transaction(['images'], 'readwrite');
          const store = transaction.objectStore('images');
          
          const item = {
            id: key,
            dataUrl: imageDataUrl,
            timestamp: Date.now()
          };
          
          const storeRequest = store.put(item);
          
          storeRequest.onsuccess = () => resolve(true);
          storeRequest.onerror = () => reject(new Error('Errore salvataggio immagine in cache'));
        };
        
        request.onerror = () => reject(new Error('Errore apertura database'));
      });
    } catch (error) {
      console.error('Errore durante il caching dell\'immagine:', error);
      return false;
    }
  },
  
  // Recupera immagine dalla cache locale
  getImageFromCache: async (key) => {
    try {
      if (!('indexedDB' in window)) {
        throw new Error('IndexedDB non supportato dal browser');
      }
      
      return new Promise((resolve, reject) => {
        const request = indexedDB.open('CelticDruidMuseum', 1);
        
        request.onsuccess = (event) => {
          const db = event.target.result;
          const transaction = db.transaction(['images'], 'readonly');
          const store = transaction.objectStore('images');
          
          const getRequest = store.get(key);
          
          getRequest.onsuccess = () => {
            const result = getRequest.result;
            resolve(result ? result.dataUrl : null);
          };
          
          getRequest.onerror = () => reject(new Error('Errore recupero immagine dalla cache'));
        };
        
        request.onerror = () => reject(new Error('Errore apertura database'));
      });
    } catch (error) {
      console.error('Errore durante il recupero dell\'immagine dalla cache:', error);
      return null;
    }
  }
};

// Utilità per la gestione dei QR Code (implementazione base)
export const qrCodeUtils = {
  // Simula la lettura di un QR code dall'input video
  decodeQrFromVideo: async (videoElement) => {
    // Questa è una funzione di esempio - in un'implementazione reale
    // utilizzeresti una libreria come jsQR o un servizio di back-end
    
    // Crea un canvas per catturare frame dal video
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    // Definisci dimensioni del canvas basate sul video
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    
    // Disegna il frame corrente del video sul canvas
    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    
    // In un'app reale, qui passeresti l'immagine a una libreria di decodifica QR
    // Per questa demo, simuliamo il trovamento di un QR code dopo un ritardo
    return new Promise((resolve) => {
      setTimeout(() => {
        // Simula il trovamento di un QR code con ID casuale
        const mockQrCodes = [
          { id: "qr1", photoId: 1 },
          { id: "qr2", photoId: 4 },
          { id: "qr3", photoId: 5 },
          { id: "qr4", photoId: 7 }
        ];
        
        const randomIndex = Math.floor(Math.random() * mockQrCodes.length);
        resolve(mockQrCodes[randomIndex]);
      }, 2000); // Simula un ritardo di 2 secondi per la scansione
    });
  },
  
  // Verifica se la fotocamera è disponibile
  isCameraAvailable: async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some(device => device.kind === 'videoinput');
    } catch (err) {
      console.error('Errore controllo disponibilità fotocamera:', err);
      return false;
    }
  },
  
  // Ottieni stream della fotocamera
  getCameraStream: async (facingMode = 'environment') => {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { facingMode }
      });
    } catch (err) {
      console.error('Errore accesso fotocamera:', err);
      throw new Error('Impossibile accedere alla fotocamera');
    }
  }
};

// Utilità per la gestione della connettività
export const networkUtils = {
  // Controlla se l'utente è online
  isOnline: () => {
    return navigator.onLine;
  },
  
  // Aggiungi listener per i cambiamenti di stato della rete
  addNetworkListeners: (onlineCallback, offlineCallback) => {
    window.addEventListener('online', onlineCallback);
    window.addEventListener('offline', offlineCallback);
    
    // Restituisci una funzione per rimuovere i listener
    return () => {
      window.removeEventListener('online', onlineCallback);
      window.removeEventListener('offline', offlineCallback);
    };
  },
  
  // Controlla se il browser supporta l'API Cache per PWA
  isCacheAPISupported: () => {
    return 'caches' in window;
  }
};

// Utilità per la formattazione date
export const dateUtils = {
  // Formatta data in formato leggibile italiano
  formatDate: (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  },
  
  // Formatta anno
  formatYear: (year) => {
    return `${year}`;
  },
  
  // Calcola anni trascorsi da una data
  yearsSince: (year) => {
    const currentYear = new Date().getFullYear();
    return currentYear - year;
  }
};

// Utilità per la condivisione sui social
export const shareUtils = {
  // Condividi su Facebook
  shareToFacebook: (url, title) => {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  },
  
  // Condividi su Twitter/X
  shareToTwitter: (url, title) => {
    const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  },
  
  // Condividi su WhatsApp
  shareToWhatsApp: (url, title) => {
    const shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${title} ${url}`)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  },
  
  // Condividi via Email
  shareViaEmail: (url, title, emailBody) => {
    const body = emailBody || `${title}\n\n${url}`;
    const shareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
    window.location.href = shareUrl;
  },
  
  // Copia link negli appunti
  copyToClipboard: (text) => {
    return new Promise((resolve, reject) => {
      try {
        navigator.clipboard.writeText(text)
          .then(() => resolve(true))
          .catch(err => {
            console.error('Errore durante la copia negli appunti:', err);
            reject(err);
          });
      } catch (err) {
        // Fallback per browser più vecchi
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          if (successful) {
            resolve(true);
          } else {
            reject(new Error('Copia non riuscita'));
          }
        } catch (err) {
          document.body.removeChild(textArea);
          reject(err);
        }
      }
    });
  },
  
  // Condividi un'immagine generata
  shareImage: (imageDataUrl, platform) => {
    // Converte il Data URL in un Blob
    const getImageBlob = (dataUrl) => {
      const byteString = atob(dataUrl.split(',')[1]);
      const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      
      return new Blob([ab], { type: mimeString });
    };
    
    // Crea un URL per il blob
    const blob = getImageBlob(imageDataUrl);
    const blobUrl = URL.createObjectURL(blob);
    
    switch (platform) {
      case 'facebook':
        // Per Facebook dobbiamo utilizzare l'API di condivisione che richiede un URL pubblico
        alert('Per condividere su Facebook, salva l\'immagine e caricala manualmente');
        break;
        
      case 'twitter':
        // Twitter ha le stesse limitazioni di Facebook
        alert('Per condividere su Twitter, salva l\'immagine e caricala manualmente');
        break;
        
      case 'whatsapp':
        // WhatsApp Web può accettare blobUrl su alcuni browser
        window.open(`https://wa.me/?text=${encodeURIComponent('Il mio ricordo dal Celtic Druid Irish Pub')}`);
        break;
        
      case 'download':
        // Crea un link per il download e simula il click
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `celtic-druid-memory-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        break;
        
      default:
        return blobUrl;
    }
    
    // Pulizia URL oggetto dopo l'uso
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    
    return blobUrl;
  }
};